/**
 * Security incidents — admin triage queue.
 *
 * Guards raise incidents from /v1/security-incidents (Phase 2 mobile work).
 * This page lets admins filter by status / severity / type, then update
 * the row through investigating / resolved / dismissed with optional
 * resolution notes.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Camera as CameraIcon,
  MapPin,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { get, patch, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { TabsPills } from '@/components/ui/tabs-pills';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateTime } from '@/lib/format';

type Status = 'open' | 'investigating' | 'resolved' | 'dismissed';
type Severity = 'low' | 'medium' | 'high' | 'critical';
type IncidentType =
  | 'theft'
  | 'suspicious_person'
  | 'suspicious_vehicle'
  | 'noise_complaint'
  | 'vandalism'
  | 'fire_safety'
  | 'medical'
  | 'trespass'
  | 'other';

interface Incident {
  id: string;
  type: IncidentType;
  severity: Severity;
  status: Status;
  title: string;
  description: string | null;
  location_hint: string | null;
  photo_url: string | null;
  occurred_at: string;
  raised_by_user_id: string;
  raised_by_role: string;
  resolution_notes: string | null;
}

const TYPE_LABEL: Record<IncidentType, string> = {
  theft: 'Theft',
  suspicious_person: 'Suspicious person',
  suspicious_vehicle: 'Suspicious vehicle',
  noise_complaint: 'Noise complaint',
  vandalism: 'Vandalism',
  fire_safety: 'Fire / Safety',
  medical: 'Medical',
  trespass: 'Trespass',
  other: 'Other'
};

const SEVERITY_COLOR: Record<Severity, string> = {
  low: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  critical: 'bg-red-100 text-red-800 border-red-200'
};

const STATUS_COLOR: Record<Status, string> = {
  open: 'bg-amber-100 text-amber-800 border-amber-200',
  investigating: 'bg-sky-100 text-sky-800 border-sky-200',
  resolved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  dismissed: 'bg-slate-100 text-slate-600 border-slate-200'
};

export function SecurityIncidentsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<Status | 'all'>('all');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [nextStatus, setNextStatus] = useState<Status>('investigating');

  const incidentsQ = useQuery({
    queryKey: ['security-incidents', statusFilter],
    queryFn: async () => {
      const params: Record<string, string> = { limit: '100' };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await get<{ items: Incident[] }>('/v1/security-incidents', { params });
      return res.items;
    }
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, status, resolution_notes }: { id: string; status: Status; resolution_notes?: string }) => {
      await patch(`/v1/security-incidents/${id}`, { status, ...(resolution_notes ? { resolution_notes } : {}) });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['security-incidents'] });
      setUpdatingId(null);
      setNotes('');
      toast.success('Incident updated');
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
    }
  });

  const open = updatingId ? incidentsQ.data?.find((i) => i.id === updatingId) : null;

  const summary = useMemo(() => {
    const all = incidentsQ.data ?? [];
    return {
      total: all.length,
      open: all.filter((i) => i.status === 'open').length,
      investigating: all.filter((i) => i.status === 'investigating').length,
      critical: all.filter((i) => i.severity === 'critical').length
    };
  }, [incidentsQ.data]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Security incidents"
        description="Triage incidents raised by guards on patrol."
      />

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <KpiCard label="Total" value={summary.total} tone="default" />
        <KpiCard label="Open" value={summary.open} tone="warn" />
        <KpiCard label="Investigating" value={summary.investigating} tone="info" />
        <KpiCard label="Critical" value={summary.critical} tone="alert" />
      </div>

      <TabsPills
        value={statusFilter}
        onChange={(v) => setStatusFilter(v as Status | 'all')}
        options={[
          { value: 'all', label: 'All' },
          { value: 'open', label: 'Open' },
          { value: 'investigating', label: 'Investigating' },
          { value: 'resolved', label: 'Resolved' },
          { value: 'dismissed', label: 'Dismissed' }
        ]}
      />

      {incidentsQ.isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : (incidentsQ.data ?? []).length === 0 ? (
        <EmptyState
          title="No incidents in this view"
          description="Switch tabs or wait for guards to raise something."
          icon={<CheckCircle2 className="h-10 w-10 text-emerald-500" />}
        />
      ) : (
        <div className="space-y-3">
          {(incidentsQ.data ?? []).map((it) => (
            <Card key={it.id} className="overflow-hidden">
              <CardContent className="p-4 flex gap-4 items-start">
                {it.photo_url ? (
                  <img src={it.photo_url} alt="" className="w-20 h-20 rounded-lg object-cover flex-none" />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-slate-100 flex items-center justify-center flex-none">
                    <CameraIcon className="h-6 w-6 text-slate-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Badge className={SEVERITY_COLOR[it.severity]}>{it.severity}</Badge>
                    <Badge className={STATUS_COLOR[it.status]}>{it.status}</Badge>
                    <span className="text-xs text-slate-500">{TYPE_LABEL[it.type]}</span>
                  </div>
                  <h3 className="font-semibold text-slate-900 truncate">{it.title}</h3>
                  {it.description ? <p className="text-sm text-slate-600 mt-1 line-clamp-2">{it.description}</p> : null}
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                    {it.location_hint ? (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {it.location_hint}
                      </span>
                    ) : null}
                    <span>{formatDateTime(it.occurred_at)}</span>
                    <span>{it.raised_by_role}</span>
                  </div>
                  {it.resolution_notes ? (
                    <p className="text-xs text-slate-700 mt-2 italic">Notes: {it.resolution_notes}</p>
                  ) : null}
                </div>
                <div className="flex flex-col gap-2 flex-none">
                  {it.status === 'open' ? (
                    <Button
                      size="sm"
                      onClick={() => {
                        setUpdatingId(it.id);
                        setNextStatus('investigating');
                        setNotes('');
                      }}
                    >
                      Take up
                    </Button>
                  ) : null}
                  {it.status === 'investigating' ? (
                    <Button
                      size="sm"
                      onClick={() => {
                        setUpdatingId(it.id);
                        setNextStatus('resolved');
                        setNotes('');
                      }}
                    >
                      Resolve
                    </Button>
                  ) : null}
                  {(it.status === 'open' || it.status === 'investigating') ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setUpdatingId(it.id);
                        setNextStatus('dismissed');
                        setNotes('');
                      }}
                    >
                      Dismiss
                    </Button>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!open} onOpenChange={(v) => !v && setUpdatingId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {nextStatus === 'investigating' ? 'Take up incident' : nextStatus === 'resolved' ? 'Resolve incident' : 'Dismiss incident'}
            </DialogTitle>
          </DialogHeader>
          {open ? (
            <div className="space-y-3">
              <div className="text-sm text-slate-700">
                <strong>{open.title}</strong>
                <p className="text-slate-500 mt-1">{TYPE_LABEL[open.type]} · {formatDateTime(open.occurred_at)}</p>
              </div>
              <div>
                <Label htmlFor="notes">
                  {nextStatus === 'resolved' ? 'Resolution notes (required)' : 'Notes (optional)'}
                </Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={nextStatus === 'resolved' ? 'What was done? Who was involved?' : 'Brief reason…'}
                  rows={4}
                />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUpdatingId(null)}>Cancel</Button>
            <Button
              disabled={nextStatus === 'resolved' && notes.trim().length < 3}
              onClick={() => {
                if (!updatingId) return;
                updateMut.mutate({ id: updatingId, status: nextStatus, resolution_notes: notes.trim() || undefined });
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, value, tone }: { label: string; value: number; tone: 'default' | 'warn' | 'info' | 'alert' }) {
  const toneClass =
    tone === 'warn' ? 'border-amber-200' : tone === 'info' ? 'border-sky-200' : tone === 'alert' ? 'border-red-200' : 'border-slate-200';
  const valueClass =
    tone === 'warn' ? 'text-amber-700' : tone === 'info' ? 'text-sky-700' : tone === 'alert' ? 'text-red-700' : 'text-slate-900';
  const Icon = tone === 'alert' ? AlertTriangle : tone === 'info' ? CheckCircle2 : tone === 'warn' ? AlertTriangle : XCircle;
  return (
    <Card className={toneClass}>
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase text-slate-500 font-semibold tracking-wide">{label}</p>
          <p className={`text-2xl font-bold ${valueClass}`}>{value}</p>
        </div>
        <Icon className={`h-5 w-5 ${valueClass}`} />
      </CardContent>
    </Card>
  );
}
